import {DateOnlyInput} from '../components/DateOnlyInput';
import {formatDateOnly} from '../dates';
import { ApiError } from "../api";
import { useEffect, useRef, useState } from "react";
import type { PayableApi } from "./Payables";
import { ErrorState } from "../ui/State";
export function RecurringPayables({
  invoice,
  request,
  canManage,
}: {
  invoice: any;
  request: PayableApi;
  canManage: boolean;
}) {
  const [page, setPage] = useState(1),
    [occurrencePage, setOccurrencePage] = useState(1);
  const [data, setData] = useState<any>(),
    [error, setError] = useState<unknown>(),
    [revision, setRevision] = useState(0),
    [busy, setBusy] = useState(false),
    [notice, setNotice] = useState("");
  const lock = useRef(false),
    pending = useRef<{ path: string; payload: any } | undefined>(undefined);
  const scoped = (path: string) =>
    path +
    (path.includes("?") ? "&" : "?") +
    "building=" +
    invoice.development_id;
  useEffect(() => {
    let alive = true;
    void request(
      scoped(`/recurring?page=${page}&occurrencePage=${occurrencePage}`),
    )
      .then((r) => {
        if (alive) setData(r);
      })
      .catch((e) => {
        if (alive) setError(e);
      });
    return () => {
      alive = false;
    };
  }, [invoice.development_id, revision, page, occurrencePage]);
  const send = async (path: string, payload: any) => {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    setError(undefined);
    pending.current ??= {
      path,
      payload: { ...payload, idempotencyKey: crypto.randomUUID() },
    };
    try {
      await request(scoped(pending.current.path), {
        method: "POST",
        body: JSON.stringify(pending.current.payload),
      });
      pending.current = undefined;
      setNotice(
        "Recurring bill change saved. No invoice, posting or payment was created.",
      );
      setRevision((r) => r + 1);
    } catch (e) {
      if (e instanceof ApiError && e.statusCode > 0 && e.statusCode < 500)
        pending.current = undefined;
      setError(e);
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  return (
    <section className="panel">
      <h3>Recurring supplier bills</h3>
      <p>
        Prepare expected bills from a posted invoice. Each occurrence needs its
        own real supplier invoice and the usual review, approval and posting.
      </p>
      {notice && <p role="status">{notice}</p>}
      {Boolean(error) && <ErrorState error={error} />}
      <fieldset disabled={busy || Boolean(pending.current)}>
        {canManage &&
          invoice.posting_status === "Posted" &&
          !invoice.is_credit_note && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const f = new FormData(e.currentTarget);
                void send("/recurring", {
                  templateInvoiceId: invoice.id,
                  label: String(f.get("label")),
                  startsOn: String(f.get("startsOn")),
                  endsOn: String(f.get("endsOn")) || null,
                  intervalMonths: Number(f.get("months")),
                  reason: String(f.get("reason")),
                });
              }}
            >
              <div className="form-grid">
                <label>
                  Schedule name
                  <input name="label" required maxLength={160} />
                </label>
                <label>
                  First expected date
                  <DateOnlyInput name="startsOn"  required />
                </label>
                <label>
                  Last expected date, if known
                  <DateOnlyInput name="endsOn"  />
                </label>
                <label>
                  Repeat every
                  <select name="months">
                    <option value="1">Month</option>
                    <option value="3">Quarter</option>
                    <option value="6">Six months</option>
                    <option value="12">Year</option>
                  </select>
                </label>
                <label>
                  Reason
                  <input name="reason" required minLength={3} />
                </label>
              </div>
              <button>Create recurring schedule</button>
            </form>
          )}
        {data?.rows.map((r: any) => (
          <article className="panel" key={r.id}>
            <strong>
              {r.label} · {r.supplier_name}
            </strong>
            <p>
              {r.status} · every {r.interval_months} months from {formatDateOnly(r.starts_on)}
            </p>
            {canManage && (
              <>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const f = new FormData(e.currentTarget);
                    void send(`/recurring/${r.id}/prepare`, {
                      through: String(f.get("through")),
                      expectedVersion: r.version,
                      reason: String(f.get("reason")),
                    });
                  }}
                >
                  <label>
                    Prepare expected bills through
                    <DateOnlyInput  name="through" required />
                  </label>
                  <label>
                    Reason
                    <input name="reason" required minLength={3} />
                  </label>
                  <button disabled={r.status !== "Active"}>
                    Prepare expected bills
                  </button>
                </form>
                <button
                  onClick={() =>
                    void send(`/recurring/${r.id}/status`, {
                      expectedVersion: r.version,
                      status: r.status === "Active" ? "Paused" : "Active",
                      reason: "Reviewed recurring schedule status",
                    })
                  }
                >
                  {r.status === "Active" ? "Pause" : "Resume"} schedule
                </button>
              </>
            )}
          </article>
        ))}
        <div className="actions">
          <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
            Previous schedules
          </button>
          <button
            disabled={!data?.hasMore}
            onClick={() => setPage((p) => p + 1)}
          >
            Next schedules
          </button>
        </div>
        <h4>Expected bills awaiting an invoice</h4>
        <div className="actions">
          <button
            disabled={occurrencePage === 1}
            onClick={() => setOccurrencePage((p) => p - 1)}
          >
            Previous expected bills
          </button>
          <button
            disabled={!data?.occurrencesHaveMore}
            onClick={() => setOccurrencePage((p) => p + 1)}
          >
            Next expected bills
          </button>
        </div>
        <p>
          {data?.occurrencesHaveMore
            ? "More expected bills are available on the next page."
            : ""}
        </p>
        {data?.occurrences.map((x: any) => (
          <article key={x.id}>
            <strong>
              {x.label} · {formatDateOnly(x.expected_on)}
            </strong>
            {canManage && x.template_snapshot?.template_invoice_id!==invoice.id && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const f = new FormData(e.currentTarget);
                  void send(`/recurring/occurrences/${x.id}/match`, {
                    invoiceId: invoice.id,
                    reason: String(f.get("reason")),
                  });
                }}
              >
                <label>
                  Reason for linking this invoice
                  <input name="reason" required minLength={3} />
                </label>
                <button>Link this real invoice</button>
              </form>
            )}
          </article>
        ))}
      </fieldset>
      {pending.current && !busy && (
        <button
          onClick={() =>
            void send(pending.current!.path, pending.current!.payload)
          }
        >
          Retry the same change
        </button>
      )}
    </section>
  );
}
